import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { WhatsAppDeviceService, InstanceListItem } from '@shared/services/whatsapp-device.service';
import { WhatsAppQrConnectedPayload } from '../whatsapp-qr-modal/whatsapp-qr-modal.component';

export interface WhatsAppDevicePanelChange {
  instanceId: string | null;
  accountId: string | null;
}

@Component({
  selector: 'app-whatsapp-device-panel',
  templateUrl: './whatsapp-device-panel.component.html',
  styleUrl: './whatsapp-device-panel.component.scss',
})
export class WhatsAppDevicePanelComponent implements OnChanges, OnDestroy, AfterViewInit {
  @Input() instanceId: string | null = null;
  /** School edit: persist consent via API before QR. */
  @Input() schoolId: number | null = null;
  @Input() readOnly = false;

  @Output() deviceChange = new EventEmitter<WhatsAppDevicePanelChange>();
  @Output() chatvionBillingAcknowledged = new EventEmitter<void>();
  @Output() consentPersistError = new EventEmitter<string>();

  @ViewChild('qrModalHeader', { static: true }) qrModalHeader!: TemplateRef<unknown>;
  @ViewChild('qrModalBody', { static: true }) qrModalBody!: TemplateRef<unknown>;
  @ViewChild('ackModalHeader', { static: true }) ackModalHeader!: TemplateRef<unknown>;
  @ViewChild('ackModalBody', { static: true }) ackModalBody!: TemplateRef<unknown>;
  @ViewChild('ackModalFooter', { static: true }) ackModalFooter!: TemplateRef<unknown>;

  device: InstanceListItem | null = null;
  loading = false;
  loadError: string | null = null;
  showQrModal = false;
  showAckModal = false;
  qrModalAccountId: string | undefined;
  qrModalTemplate!: { header: TemplateRef<unknown>; body: TemplateRef<unknown> };
  ackModalTemplate!: { header: TemplateRef<unknown>; body: TemplateRef<unknown>; footer: TemplateRef<unknown> };
  qrModalSize = { width: '400px', height: '450px' };
  ackModalSize = { width: '520px', height: '340px' };
  removing = false;

  /** After QR link, list API can lag; do not clear parent form until this time or device appears. */
  private skipNotFoundClearUntil = 0;
  private notFoundRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly notFoundRetryMs = 2000;
  private readonly postQrListGraceMs = 25000;

  constructor(private deviceService: WhatsAppDeviceService) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.clearNotFoundRetryTimer();
    if (changes['instanceId'] && changes['instanceId'].currentValue == null) {
      this.skipNotFoundClearUntil = 0;
    }
    if (this.instanceId) {
      this.resolveAndLoadDevice();
    } else {
      this.device = null;
      this.loadError = null;
    }
  }

  ngAfterViewInit(): void {
    this.qrModalTemplate = { header: this.qrModalHeader, body: this.qrModalBody };
    this.ackModalTemplate = {
      header: this.ackModalHeader,
      body: this.ackModalBody,
      footer: this.ackModalFooter,
    };
  }

  ngOnDestroy(): void {
    this.clearNotFoundRetryTimer();
    this.showQrModal = false;
  }

  private clearNotFoundRetryTimer(): void {
    if (this.notFoundRetryTimer != null) {
      clearTimeout(this.notFoundRetryTimer);
      this.notFoundRetryTimer = null;
    }
  }

  closeQrModal(): void {
    this.showQrModal = false;
  }

  private resolveAndLoadDevice(): void {
    if (!this.instanceId) {
      this.device = null;
      return;
    }
    this.loading = true;
    this.loadError = null;
    this.deviceService.listInstances().subscribe({
      next: (data) => {
        const found = data.instances.find((i) => i.instanceId === this.instanceId);
        this.device = found ?? null;
        if (found) {
          this.skipNotFoundClearUntil = 0;
        }
        if (!found) {
          const inListGrace = Date.now() < this.skipNotFoundClearUntil;
          this.loadError = inListGrace
            ? 'Finalising link — waiting for device to appear in Chatvion…'
            : 'Device not found. It may have been removed or is from another tenant.';
          // Clear stale InstanceID so Save can soft-delete — but not while Chatvion list may still be catching up after QR.
          if (!this.readOnly) {
            if (inListGrace) {
              this.notFoundRetryTimer = setTimeout(() => {
                this.notFoundRetryTimer = null;
                this.resolveAndLoadDevice();
              }, this.notFoundRetryMs);
              this.loading = true;
              return;
            }
            this.deviceChange.emit({ instanceId: null, accountId: null });
          }
        }
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.loadError = err?.error?.message ?? err?.message ?? 'Failed to load devices';
        this.device = null;
      },
    });
  }

  onAddDevice(): void {
    this.showAckModal = true;
  }

  onAckCancel(): void {
    this.showAckModal = false;
  }

  onAckAgree(): void {
    this.showAckModal = false;
    this.qrModalAccountId = undefined;
    this.showQrModal = true;
  }

  onReconnect(): void {
    if (!this.device?.id) return;
    this.qrModalAccountId = this.device.id;
    this.showQrModal = true;
  }

  onQrConnected(payload: WhatsAppQrConnectedPayload): void {
    this.showQrModal = false;
    if (payload.instanceId) {
      this.skipNotFoundClearUntil = Date.now() + this.postQrListGraceMs;
    }
    this.deviceChange.emit({
      instanceId: payload.instanceId || null,
      accountId: payload.accountId || null,
    });
    if (payload.instanceId) {
      this.instanceId = payload.instanceId;
      this.resolveAndLoadDevice();
    }
  }

  onQrClosed(): void {
    this.showQrModal = false;
  }

  onLogout(): void {
    if (!this.device?.id) return;
    this.loading = true;
    this.deviceService.logoutInstance(this.device.id).subscribe({
      next: () => {
        this.loading = false;
        this.resolveAndLoadDevice();
      },
      error: (err) => {
        this.loading = false;
        this.loadError = err?.error?.message ?? err?.message ?? 'Logout failed';
      },
    });
  }

  onRemove(): void {
    if (!this.device?.id || !confirm('Remove this WhatsApp device from the school? This cannot be undone.')) {
      return;
    }
    this.removing = true;
    this.deviceService.deleteInstance(this.device.id).subscribe({
      next: () => {
        this.removing = false;
        this.skipNotFoundClearUntil = 0;
        this.device = null;
        this.instanceId = null;
        this.deviceChange.emit({ instanceId: null, accountId: null });
      },
      error: (err) => {
        this.removing = false;
        this.loadError = err?.error?.message ?? err?.message ?? 'Remove failed';
      },
    });
  }

  get statusLabel(): string {
    if (!this.device) return '';
    const s = this.device.status;
    if (s === 'CONNECTED') return 'Connected';
    if (s === 'LOGGED_OUT' || s === 'DISCONNECTED' || s === 'NEEDS_QR') return 'Disconnected';
    return s;
  }

  get isConnected(): boolean {
    return this.device?.status === 'CONNECTED';
  }

  get showEmptyState(): boolean {
    return !this.device && !this.loading;
  }
}
